/**
 * Measure tool units. Every position is stored in CSS px; the unit only
 * changes how distances are labelled, so switching it never moves anything.
 *
 *   px            — absolute CSS px (the same numbers as the code)
 *   rem           — font-size of the lobby <html> element
 *   em            — font-size of the reference element (the last wrapped one);
 *                   falls back to rem until an element is wrapped
 *   %             — viewport size along the measured axis
 *   vw / vh       — viewport width / height
 *   dvh           — dynamic viewport height (follows the iOS address bar)
 */

const ALL_UNITS = ['px', 'rem', 'em', '%', 'vw', 'vh', 'dvh'] as const;

export type MeasureUnit = (typeof ALL_UNITS)[number];

/** `x` = horizontal distances (along the width), `y` = vertical ones. */
export type MeasureAxis = 'x' | 'y';

/** The element em is measured against — the last one wrapped with Alt+click. */
export interface MeasureReference {
    /** Short selector, e.g. `div.GameCard_card__x9y8`. */
    label: string;
    width: number;
    height: number;
    fontSize: number;
}

/** Everything formatting a distance depends on. MeasureState extends this, so callers pass the state. */
export interface UnitSettings {
    unit: MeasureUnit;
    reference: MeasureReference | null;
}

export function isMeasureUnit(value: unknown): value is MeasureUnit {
    return typeof value === 'string' && (ALL_UNITS as readonly string[]).includes(value);
}

export function viewportSizeOn(axis: MeasureAxis): number {
    return axis === 'x' ? window.innerWidth : window.innerHeight;
}

export function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

export function fontSizeOf(element: Element = document.documentElement): number {
    const size = parseFloat(getComputedStyle(element).fontSize);
    return size > 0 ? size : 16;
}

/** dvh can't be computed from window sizes — read it off a hidden probe. Cached per viewport size. */
const viewportHeightCache = new Map<string, number>();

function dynamicViewportHeight(): number {
    const key = `${window.innerWidth}x${window.innerHeight}`;
    const cached = viewportHeightCache.get(key);
    if (cached !== undefined) return cached;

    const probe = document.createElement('div');
    probe.style.cssText = `position:fixed;top:0;left:0;width:0;height:100dvh;visibility:hidden;pointer-events:none`;
    document.body.appendChild(probe);
    // Browsers without dvh ignore the height — fall back to the layout viewport.
    const height = probe.offsetHeight || window.innerHeight;
    probe.remove();
    viewportHeightCache.set(key, height);
    return height;
}

/** How many CSS px one unit is worth on the given axis, right now. */
function pixelsPerUnit({ unit, reference }: UnitSettings, axis: MeasureAxis): number {
    switch (unit) {
        case 'px':
            return 1;
        case 'rem':
            return fontSizeOf();
        case 'em':
            return reference ? reference.fontSize : fontSizeOf();
        case '%':
            return viewportSizeOn(axis) / 100;
        case 'vw':
            return window.innerWidth / 100;
        case 'vh':
            return window.innerHeight / 100;
        case 'dvh':
            return dynamicViewportHeight() / 100;
    }
}

/** `12.50` → `"12.5"`: rounds and drops trailing zeros. */
export function formatNumber(value: number, decimals = 2): string {
    return String(Number(value.toFixed(decimals)));
}

/** A px distance in the current unit, e.g. `123.5px`, `7.719rem`, `31.6%`. */
export function formatDistance(pixels: number, settings: UnitSettings, axis: MeasureAxis): string {
    const perUnit = pixelsPerUnit(settings, axis);
    // A collapsed (0-size) viewport would otherwise print "Infinity%".
    const value = perUnit > 0 ? pixels / perUnit : 0;
    const decimals = settings.unit === 'rem' || settings.unit === 'em' ? 3 : 2;
    return formatNumber(value, decimals) + settings.unit;
}

interface RulerTicks {
    /** Distance between two small ticks, in px. */
    stepPixels: number;
    /** Value of one small step, in the display unit. */
    stepValue: number;
    /** Every Nth tick is a long one. */
    majorEvery: number;
    /** Every Nth tick gets a number (always a multiple of `majorEvery`). */
    labelEvery: number;
}

/** "Nice" small-tick steps (in display units) and how many make a long tick. */
const TICK_STEPS = [
    { step: 0.1, majorEvery: 5 },
    { step: 0.25, majorEvery: 4 },
    { step: 0.5, majorEvery: 2 },
    { step: 1, majorEvery: 5 },
    { step: 2, majorEvery: 5 },
    { step: 5, majorEvery: 2 },
    { step: 10, majorEvery: 5 },
    { step: 20, majorEvery: 5 },
    { step: 50, majorEvery: 2 },
    { step: 100, majorEvery: 5 },
];
/** Closer than this, ticks blur into a solid band. */
const MIN_TICK_GAP = 4;
/** Closer than this, numbers overlap. */
const MIN_LABEL_GAP = 30;

/** Tick spacing that stays readable in any unit and viewport size. */
export function rulerTicks(settings: UnitSettings, axis: MeasureAxis): RulerTicks {
    const perUnit = pixelsPerUnit(settings, axis);
    const { step, majorEvery } =
        TICK_STEPS.find((option) => option.step * perUnit >= MIN_TICK_GAP) ??
        TICK_STEPS[TICK_STEPS.length - 1];
    const stepPixels = step * perUnit;
    const majorsPerLabel =
        [1, 2, 5, 10].find((count) => stepPixels * majorEvery * count >= MIN_LABEL_GAP) ?? 10;
    return { stepPixels, stepValue: step, majorEvery, labelEvery: majorEvery * majorsPerLabel };
}
